import React, { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { PageTitle } from '../../../_4threal/layout/core'
import { useSelector } from 'react-redux'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { Card, Row, Col, Button, Badge, Alert, Spinner } from 'react-bootstrap'
import UpdatePaymentMethodModal from './modal/UpdatePaymentMethodModal'
import EnhancedPaywallModal from '../dashboard/modal/EnhancedPaywallModal'
import { generatePopup } from '../../../utils/popup'


const AccountPage: React.FC = () => {
  const userdata = useSelector((state: any) => state.Auth?.authProfile?.userData)
  const token = localStorage.getItem('userId')
  
  const [billingInfo, setBillingInfo] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showUpdatePaymentModal, setShowUpdatePaymentModal] = useState(false)
  const [showPaywall, setShowPaywall] = useState(false)
  const [paywallData, setPaywallData] = useState<{
    experiences: any[]
    trialDaysRemaining: number
    sceneCount: number
    isOnActivate: boolean
  } | null>(null)
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(false)
  const [affiliateData, setAffiliateData] = useState<any>(null)
  const [isAffiliate, setIsAffiliate] = useState(false)
  const [loadingAffiliate, setLoadingAffiliate] = useState(false)

  // Firebase functions
  const functions = getFunctions()
  const getBillingInfo = httpsCallable(functions, 'getBillingInfo')
  const checkSceneCreationPermission = httpsCallable(functions, 'checkSceneCreationPermission')
  const getActiveExperencesForPayment = httpsCallable(functions, 'getActiveExperencesForPayment')
  const getAffiliateByEmail = httpsCallable(functions, 'getAffiliateByEmail')

  useEffect(() => {
    if (token) {
      loadBillingInfo()
      loadAffiliateInfo()
    }
  }, [token])

  const loadBillingInfo = async () => {
    try {
      setLoading(true)
      const result = await getBillingInfo()
      const data = result.data as any
      
      if (data.success) {
        console.log('data', data)
        setBillingInfo(data)
      } else {
        setError(data.error || 'Failed to load billing information')
      }
    } catch (err: any) {
      console.error('Error loading billing info:', err)
      setError('Failed to load account information')
    } finally {
      setLoading(false)
    }
  }

  const loadAffiliateInfo = async () => {
    try {
      setLoadingAffiliate(true)
      const userEmail = userdata?.protected?.email
      
      if (!userEmail) {
        setIsAffiliate(false)
        return
      }

      const result = await getAffiliateByEmail({ email: userEmail })
      const data = result.data as any
      
      if (data.result === 'found' && data.isAffiliate) {
        setIsAffiliate(true)
        setAffiliateData(data.affiliateData)
        console.log('Affiliate data loaded:', data.affiliateData)
      } else {
        setIsAffiliate(false)
        setAffiliateData(null)
      }
    } catch (err: any) {
      console.error('Error loading affiliate info:', err)
      setIsAffiliate(false)
      setAffiliateData(null)
    } finally {
      setLoadingAffiliate(false)
    }
  }

  const handlePaymentMethodUpdated = (updatedPaymentMethod: any) => {
    setBillingInfo((prev: any) => ({
      ...prev,
      paymentMethod: updatedPaymentMethod
    }))
    setShowUpdatePaymentModal(false)
  }

  const handlePaywallCancel = () => {
    setShowPaywall(false)
    setPaywallData(null)
  }

  const handlePaywallComplete = async (paymentData: { success: boolean }) => {
    if (paymentData.success) {
      setShowPaywall(false)
      generatePopup('success', 'Payment setup complete!.')
      setPaywallData(null)
    }
  }

  const formatDate = (date: any) => {
    if (!date) return 'N/A'
    try {
      // Handle different date formats
      if (date.seconds) {
        // Firestore Timestamp
        return new Date(date.seconds * 1000).toLocaleDateString()
      } else if (typeof date === 'number') {
        // Unix timestamp in milliseconds
        return new Date(date).toLocaleDateString()
      } else if (date instanceof Date) {
        // JavaScript Date object
        return date.toLocaleDateString()
      } else if (typeof date === 'string') {
        // ISO string
        return new Date(date).toLocaleDateString()
      } else if (typeof date === 'object' && date !== null) {
        // Handle empty objects or other object formats
        console.warn('Unexpected date object format:', date)
        return 'N/A'
      }
      return 'N/A'
    } catch (error) {
      console.error('Error formatting date:', error, date)
      return 'N/A'
    }
  }

  const getStatusBadge = (status: string, isCanceled?: boolean) => {
    if (isCanceled) {
      return <Badge bg="danger">Canceled</Badge>
    }
    
    switch (status?.toLowerCase()) {
      case 'active':
        return <Badge bg="success">Active</Badge>
      case 'trialing':
        return <Badge bg="info">Trial</Badge>
      case 'canceled':
        return <Badge bg="danger">Canceled</Badge>
      case 'past_due':
        return <Badge bg="warning">Past Due</Badge>
      default:
        return <Badge bg="secondary">{status || 'Unknown'}</Badge>
    }
  }

  const handlePaywallCheck = async () => {
    // Admin users can always create experiences


    setIsCheckingPermissions(true)
    
    try {
      // Check if user can create a scene
      const permissionResult = await checkSceneCreationPermission()
      const permissionData = permissionResult.data as any
      console.log('permissionData', permissionData)
      
      if (permissionData.canCreate && !permissionData.requiresPreviousPayment) {
        // User can create scene normally
        setShowUpdatePaymentModal(true)
      } else if (permissionData.requiresPreviousPayment) {
        // Need to set up payment for first experience
        const scenesResult = await getActiveExperencesForPayment()
        const experienceData = scenesResult.data as any
        
        if (experienceData.needsPaymentSetup) {
          setPaywallData({
            experiences: experienceData.scenesThatNeedPayment,
            trialDaysRemaining: experienceData.trialDaysRemaining,
            sceneCount: experienceData.sceneCount,
            isOnActivate: false
          })
          setShowPaywall(true)
        } else {
          // Shouldn't happen, but fallback
            setShowUpdatePaymentModal(true)
        }
      } else {
        // User can't create scene for other reasons
        setShowUpdatePaymentModal(true)
      }
    } catch (error) {
      console.error('Error checking scene creation permissions:', error)
      generatePopup('error', 'Error checking permissions. Please try again.')
    } finally {
      setIsCheckingPermissions(false)
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  return (
    <div className="container-fluid">
      <Row>
        <Col lg={8} className="pt-4">
          {/* Account Information */}
          <Card className="mb-4">
            <Card.Header>
              <h4 className="mb-0 pt-4">Account Information</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p><strong>Name:</strong> {userdata?.firstName} {userdata?.lastName}</p>
                  <p><strong>Email:</strong> {userdata?.protected?.email}</p>
                  <p><strong>Account Created:</strong> {formatDate(userdata?.protected?.created)}</p>
                </Col>
                <Col md={6}>
                  <p><strong>User ID:</strong> {token}</p>
                  <p><strong>Account Type:</strong> {
                    userdata?.protected?.isAdmin ? 'Admin' :
                    userdata?.protected?.isB2B ? 'B2B' :
                    userdata?.protected?.isCreator ? 'Creator' :
                    userdata?.protected?.isBeta ? 'Beta' : 'Standard'
                  }</p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Trial Information */}
          {billingInfo?.trial && (
            <Card className="mb-4">
              <Card.Header>
                <h4 className="mb-0 pt-4">Trial Status</h4>
              </Card.Header>
              <Card.Body>
                {billingInfo.trial.isActive ? (
                  <Alert variant="info">
                    <strong>Trial Active!</strong> You have {billingInfo.trial.daysRemaining} days remaining in your trial.
                    <br />
                    <small>Trial ends: {formatDate(billingInfo.trial.endDate)}</small>
                  </Alert>
                ) : (
                  <Alert variant="secondary">
                    Your trial has ended. {formatDate(billingInfo.trial.endDate)}
                  </Alert>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Subscriptions */}
          <Card className="mb-4">
            <Card.Header>
              <h4 className="mb-0 pt-4">Active Subscriptions</h4>
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger">
                  {error}
                  <br />
                  <Button variant="outline-danger" size="sm" onClick={loadBillingInfo}>
                    Retry
                  </Button>
                </Alert>
              )}
              
              {billingInfo?.subscriptions && billingInfo.subscriptions.length > 0 ? (
                <div className="space-y-3">
                  {billingInfo.subscriptions.map((sub: any, index: number) => (
                    <Card key={index} className="border-start border-primary border-3">
                      <Card.Body>
                        <Row>
                          <Col md={8}>
                            <h6>{sub.sceneName || `Experience ${index + 1}`}</h6>
                            <p className="text-muted mb-1">
                              {sub.planType == 'year' ? 'Yearly Plan' : 'Monthly Plan'} - 
                              ${`${sub.amount}/${sub.planType}`}
                            </p>
                                <p className="text-muted mb-0">
                                  {sub.isCanceled ? 
                                    `Canceled - Access until: ${formatDate(sub.currentPeriodEnd)}` :
                                    sub.status === 'trialing' && sub.trialEnd ? 
                                      `Trial ends: ${formatDate(sub.trialEnd)}` :
                                      `Next billing: ${formatDate(sub.currentPeriodEnd)}`
                                  }
                                </p>
                          </Col>
                          <Col md={4} className="text-end">
                            {getStatusBadge(sub.status, sub.isCanceled)}
                            {sub.trialEnd && new Date(sub.trialEnd) > new Date() && (
                              <div className="mt-1">
                                <Badge bg="info" className="text-dark">
                                  Trial until {formatDate(sub.trialEnd)}
                                </Badge>
                              </div>
                            )}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              ) : (
                <Alert variant="light">
                  No active subscriptions found. Create an experience to get started!
                </Alert>
              )}
            </Card.Body>
          </Card>

          {/* Affiliate Information */}
          {isAffiliate && affiliateData && (
            <Card className="mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0 pt-4">Affiliate Information</h4>
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={loadAffiliateInfo}
                  disabled={loadingAffiliate}
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  {loadingAffiliate ? 'Loading...' : 'Refresh'}
                </Button>
              </Card.Header>
              <Card.Body>
                {loadingAffiliate ? (
                  <div className="d-flex justify-content-center">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Loading affiliate data...</span>
                  </div>
                ) : (
                  <div>
                    {/* Basic Affiliate Info */}
                    <Row className="mb-4">
                      <Col md={6}>
                        <p><strong>Affiliate Name:</strong> {affiliateData.affiliateName}</p>
                        <p><strong>Affiliate ID:</strong> {affiliateData.id}</p>
                        <p><strong>Status:</strong> <Badge bg={affiliateData.isActive ? 'success' : 'danger'}>
                          {affiliateData.isActive ? 'Active' : 'Inactive'}
                        </Badge></p>
                      </Col>
                      <Col md={6}>
                        <p><strong>Offer Code:</strong> {affiliateData.offerCode || 'None'}</p>
                        <p><strong>Member Since:</strong> {formatDate(affiliateData.createdAt)}</p>
                        <p><strong>Last Activity:</strong> {formatDate(affiliateData.analytics?.lastActivityAt) || 'Never'}</p>
                      </Col>
                    </Row>

                    {/* Commission Details */}
                    <Row className="mb-4">
                      <Col>
                        <h6 className="border-bottom pb-2">Commission Details</h6>
                        {affiliateData.commission?.description && (
                          <p className="text-muted mb-3">{affiliateData.commission.description}</p>
                        )}
                        <Row>
                          <Col md={4}>
                            <div className="text-center p-3 bg-light rounded">
                              <h5 className="mb-1 text-primary">
                                {affiliateData.commission?.isPercent 
                                  ? `${affiliateData.commission.rate}%` 
                                  : `$${affiliateData.commission.rate}`}
                              </h5>
                              <small className="text-muted">Commission Rate</small>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="text-center p-3 bg-light rounded">
                              <h5 className="mb-1 text-success">
                                ${(affiliateData.analytics?.totalCommissionEarned || 0).toFixed(2)}
                              </h5>
                              <small className="text-muted">Total Earned</small>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="text-center p-3 bg-light rounded">
                              <h5 className="mb-1 text-info">
                                ${(affiliateData.analytics?.salesGenerated || 0).toFixed(2)}
                              </h5>
                              <small className="text-muted">Sales Generated</small>
                            </div>
                          </Col>
                        </Row>
                      </Col>
                    </Row>

                    {/* Analytics */}
                    <Row className="mb-4">
                      <Col>
                        <h6 className="border-bottom pb-2">Performance Analytics</h6>
                        <Row>
                          <Col md={6}>
                            <div className="text-center p-3 bg-light rounded">
                              <h5 className="mb-1 text-primary">
                                {affiliateData.analytics?.siteVisits || 0}
                              </h5>
                              <small className="text-muted">Site Visits</small>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="text-center p-3 bg-light rounded">
                              <h5 className="mb-1 text-success">
                                {affiliateData.analytics?.accountsCreated || 0}
                              </h5>
                              <small className="text-muted">Accounts Created</small>
                            </div>
                          </Col>
                        </Row>
                      </Col>
                    </Row>

                    {/* Custom Plans */}
                    {affiliateData.customPlansDetails && affiliateData.customPlansDetails.length > 0 && (
                      <Row className="mb-4">
                        <Col>
                          <h6 className="border-bottom pb-2">Your Custom Plans</h6>
                          <div className="row">
                            {affiliateData.customPlansDetails.map((plan: any, index: number) => (
                              <div key={plan.id} className="col-md-6 mb-3">
                                <Card className="border-primary">
                                  <Card.Body>
                                    <h6 className="card-title">{plan.planName}</h6>
                                    <p className="text-muted mb-2">{plan.planDescription}</p>
                                    <div className="mb-2">
                                      <strong>Monthly:</strong> {plan.monthlyPrice}<br/>
                                      {plan.yearlyPrice !== 'Not Set' && (
                                        <>
                                          <strong>Yearly:</strong> {plan.yearlyPrice}
                                          {plan.yearlySavings && (
                                            <small className="text-success"> (Save {plan.yearlySavings})</small>
                                          )}
                                        </>
                                      )}
                                      {plan.experiencesIncluded && (
                                        <div className="mt-1">
                                          <small className="text-info">
                                            <i className="bi bi-collection me-1"></i>
                                            {plan.experiencesIncluded} experience{plan.experiencesIncluded !== 1 ? 's' : ''} included
                                          </small>
                                        </div>
                                      )}
                                    </div>
                                    {plan.planFeatures && (
                                      <div className="mb-2">
                                        <small className="text-muted">Features:</small>
                                        <ul className="small mb-0">
                                          {plan.planFeatures.split(', ').map((feature: string, idx: number) => (
                                            <li key={idx}>{feature.trim()}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    <Badge bg="info">Custom Plan</Badge>
                                  </Card.Body>
                                </Card>
                              </div>
                            ))}
                          </div>
                          {affiliateData.includeStandardPlans && (
                            <Alert variant="info" className="mt-2">
                              <i className="bi bi-info-circle me-2"></i>
                              Standard plans are also available to customers using your affiliate link.
                            </Alert>
                          )}
                        </Col>
                      </Row>
                    )}

                    {/* Affiliate Link */}
                    <Row>
                      <Col>
                        <h6 className="border-bottom pb-2">Your Affiliate Link</h6>
                        <div className="input-group">
                          <input 
                            type="text" 
                            className="form-control" 
                            value={`${affiliateData.baseUrl}?aff=${affiliateData.id}`}
                            readOnly 
                          />
                          <Button 
                            variant="outline-primary" 
                            onClick={() => {
                              navigator.clipboard.writeText(`${affiliateData.baseUrl}?aff=${affiliateData.id}`)
                              generatePopup('success', 'Affiliate link copied to clipboard!')
                            }}
                          >
                            <i className="bi bi-clipboard me-1"></i>
                            Copy
                          </Button>
                        </div>
                        <small className="text-muted">
                          Share this link to earn commissions on new signups and purchases.
                        </small>
                      </Col>
                    </Row>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col lg={4} className="pt-4">
          {/* Payment Method */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0 pt-4">Payment Method</h5>
            </Card.Header>
            <Card.Body>
              {billingInfo?.paymentMethod ? (
                <div>
                  <p>
                    <i className="bi bi-credit-card me-2"></i>
                    •••• •••• •••• {billingInfo.paymentMethod.last4}
                  </p>
                  <p className="text-muted">
                    {billingInfo.paymentMethod.brand?.toUpperCase()} expires {billingInfo.paymentMethod.exp_month}/{billingInfo.paymentMethod.exp_year}
                  </p>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => setShowUpdatePaymentModal(true)}
                  >
                    Update Payment Method
                  </Button>
                </div>
              ) : (
                <Alert variant="warning">
                  <p className="mb-2">No payment method on file</p>
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => handlePaywallCheck()}
                  >
                    {
                      isCheckingPermissions ? ' Checking Permissions...' :
                      'Add Payment Method'
                    }
                  </Button>
                </Alert>
              )}
            </Card.Body>
          </Card>

          {/* Quick Actions */}
          <Card>
            <Card.Header>
              <h5 className="mb-0 pt-4">Quick Actions</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button variant="primary" href="/dashboard">
                  <i className="bi bi-plus-circle me-2"></i>
                  Create New Experience
                </Button>
                <Button variant="outline-secondary" href="/analytics/overview/all">
                  <i className="bi bi-bar-chart me-2"></i>
                  View Analytics
                </Button>
                <Button variant="outline-secondary" onClick={loadBillingInfo}>
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Refresh Account Info
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Update Payment Method Modal */}
      <EnhancedPaywallModal
            show={showPaywall}
            onHide={handlePaywallCancel}
            paywallData={paywallData}
            onPaymentComplete={handlePaywallComplete}
      />
      <UpdatePaymentMethodModal
        show={showUpdatePaymentModal}
        onHide={() => setShowUpdatePaymentModal(false)}
        onPaymentMethodUpdated={handlePaymentMethodUpdated}
        currentPaymentMethod={billingInfo?.paymentMethod}
      />
    </div>
  )
}

const AccountWrapper: React.FC = () => {
  const intl = useIntl()
  return (
    <>
      <PageTitle breadcrumbs={[]}>My Account</PageTitle>
      <AccountPage />
    </>
  )
}

export { AccountWrapper }
